import { useState, useEffect, useRef } from 'react';
import { generateClient } from 'aws-amplify/api';
import type { Schema } from '../../../amplify/data/resource';
import type { WineSummary } from '@/api/wines';
import Input from '@/components/ui/Input';

const client = generateClient<Schema>();

interface WineSearchProps {
  onSelect: (wine: WineSummary) => void;
  placeholder?: string;
  allowCreate?: boolean;
}

export default function WineSearch({ onSelect, placeholder = 'Search wine name or producer…', allowCreate = true }: WineSearchProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<WineSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); return; }

    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await client.models.Wine.list({
          filter: {
            or: [
              { name: { contains: query } },
              { producer: { contains: query } },
            ],
          },
          limit: 10,
        });
        setResults(
          (data ?? []).map((w) => ({
            id: w.id,
            name: w.name,
            producer: w.producer ?? undefined,
            vintage: w.vintage ?? undefined,
            style: w.style ?? undefined,
            region: w.region ?? undefined,
          }))
        );
      } finally {
        setLoading(false);
      }
    }, 350);

    return () => clearTimeout(timerRef.current);
  }, [query]);

  return (
    <div className="wine-search">
      <Input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={placeholder}
        type="search"
      />
      {loading && <p className="wine-search-loading">Searching…</p>}
      {results.length > 0 && (
        <ul className="wine-search-results">
          {results.map((wine) => (
            <li key={wine.id}>
              <button
                className="wine-search-result"
                onClick={() => { onSelect(wine); setQuery(''); setResults([]); }}
              >
                <span className="wine-search-name">{wine.name}</span>
                <span className="wine-search-meta">
                  {[wine.producer, wine.vintage, wine.region].filter(Boolean).join(' · ')}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {!loading && allowCreate && query.trim().length >= 2 && results.length === 0 && (
        <div className="wine-search-empty">
          <p>No matches found.</p>
          <button
            className="wine-search-create"
            onClick={() => {
              onSelect({ id: '', name: query.trim() });
              setQuery('');
            }}
          >
            + Add "{query.trim()}" manually
          </button>
        </div>
      )}
    </div>
  );
}
